import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

interface BrandStore {
  sessionId: string | null;
  selectedBrandId: string | null;
  setSelectedBrandId: (brandId: string | null) => void;
  initializeSession: (sessionId: string) => Promise<void>;
  clear: () => void;
}

type PersistedBrandStore = {
  sessionId: string | null;
  selectedBrandId: string | null;
};

// Helper function to get the storage name for a session
const getStorageName = (sessionId: string) => `brand-store-${sessionId}`;

const stripLegacyOnboardingState = (sessionId: string) => {
  try {
    const key = getStorageName(sessionId);
    const raw = localStorage.getItem(key);
    if (!raw) return;

    const parsed = JSON.parse(raw) as { state?: Record<string, unknown> };
    if (!parsed.state) return;

    delete parsed.state.brandCreationModalStep;
    delete parsed.state.brandCreationBrandId;
    localStorage.setItem(key, JSON.stringify(parsed));
  } catch (error) {
    console.error("Failed to cleanup legacy onboarding state:", error);
  }
};

// Helper function to clean up old session storage
const cleanupOldSessions = (currentSessionId: string) => {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith("brand-store-") && key !== getStorageName(currentSessionId)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch (error) {
    console.error("Failed to cleanup old sessions:", error);
  }
};

export const useBrandStore = create<BrandStore>()(
  persist<BrandStore, [], [], PersistedBrandStore>(
    (set, get, store) => ({
      sessionId: null,
      selectedBrandId: null,
      setSelectedBrandId: (brandId: string | null) => {
        set({ selectedBrandId: brandId });
      },
      initializeSession: async (sessionId: string): Promise<void> => {
        // Do nothing if sessionId is not provided or invalid
        if (!sessionId || sessionId.trim() === "") {
          return;
        }
        const normalizedSessionId = sessionId.trim();
        const storageName = getStorageName(normalizedSessionId);
        const hasPersistedState = Boolean(localStorage.getItem(storageName));

        // Clean up old sessions first
        cleanupOldSessions(normalizedSessionId);

        // Always update the storage name to ensure we're pointing to the correct storage
        store.persist.setOptions({
          name: storageName,
        });

        // Remove legacy onboarding keys from persisted state before hydrating.
        stripLegacyOnboardingState(normalizedSessionId);

        // Always rehydrate from the session-specific storage. If that session has no stored state,
        // explicitly reset the in-memory selection so the previous session cannot bleed through.
        await store.persist.rehydrate();
        set({
          sessionId: normalizedSessionId,
          selectedBrandId: hasPersistedState ? get().selectedBrandId : null,
        });
      },
      clear: () => {
        set({
          selectedBrandId: null,
        });
      },
    }),
    {
      name: "brand-store-default", // Default storage name (will be overridden by initializeSession)
      storage: createJSONStorage(() => localStorage),
      version: 2,
      migrate: (persistedState: unknown) => {
        const state = (persistedState ?? {}) as Record<string, unknown>;
        return {
          sessionId: typeof state.sessionId === "string" ? state.sessionId : null,
          selectedBrandId: typeof state.selectedBrandId === "string" ? state.selectedBrandId : null,
        };
      },
      partialize: (state) => ({
        sessionId: state.sessionId,
        selectedBrandId: state.selectedBrandId,
      }),
      skipHydration: true,
    },
  ),
);
